import React, { Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, Check, Loader2, Smartphone, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
export interface PetEntry {
  id: string;
  name: string;
  breed: string;
  age: string;
  size: string;
  bio: string;
  photoFile: File | null;
  photoUrlFromMobile: string | null;
  photoSessionId: string;
  showQR: boolean;
}
export const newPet = (): PetEntry => ({
  id: typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  name: '',
  breed: '',
  age: '',
  size: '',
  bio: '',
  photoFile: null,
  photoUrlFromMobile: null,
  photoSessionId: typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  showQR: false
});
export const validatePhotoFile = (file: File): boolean => {
  if (file.size === 0) {
    toast.error(`"${file.name}" parece estar vacío.`);
    return false;
  }
  if (file.size > 10 * 1024 * 1024) {
    toast.error(`Tu foto pesa ${(file.size / 1024 / 1024).toFixed(1)}MB. Máximo 10MB.`);
    return false;
  }
  const mime = (file.type || '').toLowerCase();
  const ext = (file.name.split('.').pop() || '').toLowerCase();
  const validExts = ['jpg', 'jpeg', 'png', 'heic', 'heif', 'webp', 'gif', 'bmp', 'tiff', 'tif'];
  const isImage = mime.startsWith('image/');
  if (!isImage && !validExts.includes(ext)) {
    toast.error(`Formato no reconocido. Usa una foto JPG, PNG, HEIC, WEBP...`);
    return false;
  }
  return true;
};
interface PetFormCardProps {
  pet: PetEntry;
  index: number;
  canRemove: boolean;
  /** Optional label override (e.g. "Mascota Principal" / "Mascota Extra #1") */
  title?: string;
  /** Optional badge shown next to title (e.g. "+$40.000") */
  badge?: React.ReactNode;
  onUpdate: (fn: (p: PetEntry) => PetEntry) => void;
  onRemove: () => void;
  onPhotoChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}
export function PetFormCard({
  pet,
  index,
  canRemove,
  title,
  badge,
  onUpdate,
  onRemove,
  onPhotoChange
}: PetFormCardProps) {
  const fileInputId = `pet-photo-${pet.id}`;
  const photoUrl = pet.photoFile ? URL.createObjectURL(pet.photoFile) : pet.photoUrlFromMobile;
  const hasPhoto = !!(pet.photoFile || pet.photoUrlFromMobile);
  const heading = title || `Mascota ${index + 1}`;
  return <div className="border border-gray-200 rounded-2xl p-5 bg-white shadow-sm" data-id="element-673" data-mp="1nmj1v1baq2et">
      <div className="flex items-center justify-between mb-4" data-id="element-674" data-mp="18x3b3lbrlwz4">
        <h4 className="font-bold text-brand-navy flex items-center gap-2" data-id="element-675" data-mp="18wk3axftwoyt">
          {heading}
          {badge}
        </h4>
        {canRemove && <button type="button" onClick={onRemove} className="text-red-500 hover:text-red-700 transition-colors p-1" title="Eliminar mascota" data-id="element-676" data-mp="21l8yygy48236">
            <Trash2 className="w-4 h-4" data-id="element-677" data-mp="3r2ct9vbfd71v" />
          </button>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[160px_1fr] gap-5" data-id="element-678" data-mp="3m3tb85zycfes">
        {/* Photo */}
        <div data-id="element-679" data-mp="4nbnbfaqg1tt">
          <label className="block text-xs font-semibold text-gray-700 mb-2" data-id="element-680" data-mp="4nthm6du45z3">
            Foto *
          </label>
          <input type="file" accept="image/*" onChange={onPhotoChange} className="hidden" id={fileInputId} data-id="element-681" data-mp="3vtcsx8bxi382" />
          <label htmlFor={fileInputId} className={`relative block w-full aspect-square rounded-xl border-2 border-dashed cursor-pointer overflow-hidden transition-colors ${hasPhoto ? 'border-brand-green' : 'border-gray-300 hover:border-brand-cyan'}`} data-id="element-682" data-mp="33577ejd1wa76">
            {photoUrl ? <>
                <img src={photoUrl} alt={pet.name || `Mascota ${index + 1}`} className="absolute inset-0 w-full h-full object-cover" data-id="element-683" data-mp="wd6e7ge8nzkl" />
                <div className="absolute top-1 right-1 w-6 h-6 bg-brand-green rounded-full flex items-center justify-center shadow" data-id="element-684" data-mp="1nodui9piwezs">
                  <Check className="w-4 h-4 text-white" data-id="element-685" data-mp="1rqh9v9fbc3qc" />
                </div>
              </> : <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400 p-2" data-id="element-686" data-mp="21mu3k7auyhl3">
                <Upload className="w-6 h-6 mb-1" data-id="element-687" data-mp="26m6lqeqsb0hc" />
                <span className="text-xs text-center font-medium" data-id="element-688" data-mp="26lf1g02n0p6d">
                  Subir foto
                </span>
                <span className="text-[10px] text-gray-400 mt-0.5" data-id="element-689" data-mp="2alfk5wwrrmj8">
                  JPG, PNG, HEIC...
                </span>
              </div>}
          </label>
          {!hasPhoto && <button type="button" onClick={() => onUpdate(p => ({
          ...p,
          showQR: !p.showQR
        }))} className="mt-2 text-brand-cyan hover:text-blue-700 text-xs font-semibold flex items-center gap-1" data-id="element-690" data-mp="2kf618gqkguow">
              <Smartphone className="w-3.5 h-3.5" data-id="element-691" data-mp="dotaqqn6olyv" />
              Subir desde otro dispositivo
            </button>}

          <AnimatePresence data-id="element-692" data-mp="8qi1oi4pqfhp">
            {pet.showQR && !hasPhoto && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} exit={{
            opacity: 0,
            height: 0
          }} className="mt-3 bg-gray-50 border border-gray-200 rounded-xl p-3 overflow-hidden" data-id="element-693" data-mp="3roo6ppfeohj">
                <p className="text-xs text-gray-600 mb-2 text-center" data-id="element-694" data-mp="mnwu7vq9j3dw">
                  Escanea con otro dispositivo
                </p>
                <img src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(`${window.location.origin}/subir-documento?session=${pet.photoSessionId}&doc=pet_photo`)}`} alt="QR Code" className="w-full max-w-[140px] mx-auto rounded-lg shadow-sm" data-id="element-695" data-mp="1fe6hox3hblcf" />
                <div className="flex items-center justify-center gap-1 text-brand-cyan text-xs font-medium mt-2" data-id="element-696" data-mp="2zud0ghqaauil">
                  <Loader2 className="w-3 h-3 animate-spin" data-id="element-697" data-mp="34t7j9jo94j2p" />
                  Esperando...
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>

        {/* Fields */}
        <div className="space-y-3" data-id="element-698" data-mp="2gxuzd9pjpi9o">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-699" data-mp="2gywlc983np66">
            <div data-id="element-700" data-mp="2lw67ng06zkps">
              <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-701" data-mp="2lxr45b62hdqa">
                Nombre *
              </label>
              <input type="text" value={pet.name} onChange={e => onUpdate(p => ({
              ...p,
              name: e.target.value
            }))} className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-brand-cyan focus:border-transparent outline-none text-sm" placeholder="Ej: Rocky" data-id="element-702" data-mp="f71dy5svm6tr" />
            </div>
            <div data-id="element-703" data-mp="29kvtelwukxkq">
              <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-704" data-mp="29kdmmvck70yk">
                Raza *
              </label>
              <input type="text" value={pet.breed} onChange={e => onUpdate(p => ({
              ...p,
              breed: e.target.value
            }))} className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-brand-cyan focus:border-transparent outline-none text-sm" placeholder="Ej: Golden" data-id="element-705" data-mp="2if0nrfb6wr50" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3" data-id="element-706" data-mp="c69lhu2em519">
            <div data-id="element-707" data-mp="c5hpwj8t47az">
              <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-708" data-mp="c3x17a96bil1">
                Edad
              </label>
              <input type="text" value={pet.age} onChange={e => onUpdate(p => ({
              ...p,
              age: e.target.value
            }))} className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-brand-cyan focus:border-transparent outline-none text-sm" placeholder="3 años" data-id="element-709" data-mp="lv17llks9v49" />
            </div>
            <div data-id="element-710" data-mp="76etfn6w79em">
              <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-711" data-mp="76wx34vvwpwk">
                Tamaño *
              </label>
              <select value={pet.size} onChange={e => onUpdate(p => ({
              ...p,
              size: e.target.value
            }))} className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-brand-cyan focus:border-transparent outline-none text-sm bg-white" data-id="element-712" data-mp="1ejoxjugh98o5">
                <option value="" data-id="element-713" data-mp="2u9g1ymrjtxjg">Selecciona...</option>
                <option value="pequeno" data-id="element-714" data-mp="2pd95dkl1lye6">Pequeño</option>
                <option value="mediano" data-id="element-715" data-mp="2pamy7m22dkwh">Mediano</option>
                <option value="grande" data-id="element-716" data-mp="2ke60c052bs67">Grande</option>
              </select>
            </div>
          </div>
          <div data-id="element-717" data-mp="2fcqe44r5ffm8">
            <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-718" data-mp="2fcyuw7qe752p">
              Bio (opcional, máx 80 caracteres)
            </label>
            <input type="text" maxLength={80} value={pet.bio} onChange={e => onUpdate(p => ({
            ...p,
            bio: e.target.value
          }))} className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-brand-cyan focus:border-transparent outline-none text-sm" placeholder="¡Hola! Soy ... y me encanta ..." data-id="element-719" data-mp="26e4x4e1bs8nk" />
            <p className="text-[10px] text-gray-400 mt-1 text-right" data-id="element-720" data-mp="x4e5mvu2d0j3">
              {pet.bio.length}/80
            </p>
          </div>
        </div>
      </div>
    </div>;
}